import { getAppConfig } from '../../../common/config/app-config';
import { APP_CONSTANTS } from '../../../common/config/app-constants';
import { getCookie, isMobileWidth } from '../../../common/util';
import { getHybrisResponseAsync } from './hybris';

let bannerCache: any[] | null = null;
let bannerPromise: Promise<any[]> | null = null;
const { lang, miraklads } = getAppConfig();
const language = lang.substr(0, 1).toUpperCase() + lang.substr(1);

export async function fetchMiraklBannersAsync(requestOpts: any): Promise<any[]> {
  // return cached data
  if (bannerCache) {
    return bannerCache;
  }

  // reuse in-flight request
  if (bannerPromise) {
    return bannerPromise;
  }

  bannerPromise = (async () => {
    try {
      const res = await getHybrisResponseAsync(APP_CONSTANTS.GET_MIRAKL_SPONSORED_PRODUCTS, {
        fields: 'FULL',
        userId: getCookie('mirakl-analytics') || '',
        ...requestOpts,
      });

      const groups = res?.displayBanners ?? [];

      const normalized = groups.map((group: any) => ({
        type: group?.creativeFormat ?? null,
        banners: group?.banners ?? [],
      }));

      // cache only on success
      bannerCache = normalized;

      return normalized;
    } catch (error) {
      console.error('Failed to fetch Mirakl banners:', error);

      // important: reset promise so retry works
      bannerPromise = null;

      // return safe fallback instead of breaking UI
      return [];
    } finally {
      //  ensure promise is cleared after resolution
      // (only if cache is already set OR error happened)
      if (bannerCache) {
        bannerPromise = null;
      }
    }
  })();

  return bannerPromise;
}

/**
 * @typedef {Object} NormalizedBanner
 * @property {string} type
 * @property {string} [redirectUrl]
 * @property {string} [imageUrl]
 * @property {string} [videoUrl]
 * @property {string} [width]
 * @property {string} [height]
 * @property {string} [htmlContent]
 * @property {Array<any>} [products]
 */

/**
 * @param {string} format
 * @returns {{width?: string, height?: string}}
 */
export function parseFormat(format) {
  if (!format || typeof format !== 'string') {
    return {};
  }

  const [width, height] = format.split(':');

  return {
    width: width || undefined,
    height: height || undefined,
  };
}

/**
 * @param {any} banner
 * @returns {NormalizedBanner}
 */
// banner-utils.js

export function normalizeBanner(banner) {
  if (!banner) {
    return {
      type: 'UNKNOWN',
      redirectUrl: '#',
      imageUrl: undefined,
      videoUrl: undefined,
      width: undefined,
      height: undefined,
      htmlContent: undefined,
      products: [],
      productIds: [],
      adId: undefined,
      adUnitId: undefined,
    };
  }

  const asset = banner.creativeAsset || {};
  const { width, height } = parseFormat(asset.format);

  const rawUrl = asset.bannerImageUrl || '';
  const isVideo = /\.mp4(\?|$)/i.test(rawUrl);

  const productIds = Array.isArray(banner.products) ? banner.products.map(p => p.productId) : [];

  return {
    type: banner.creativeFormat || 'UNKNOWN',
    redirectUrl: banner.redirectionUrl || '#',

    imageUrl: !isVideo ? rawUrl : undefined,
    videoUrl: isVideo ? rawUrl : undefined,

    width,
    height,

    htmlContent: asset.htmlContent,

    products: banner.products || [],
    productIds,

    adId: banner?.adId,
    adUnitId: banner?.adUnitId,
  };
}

// get group by type
export function getBannerGroup(groups = [], type: string) {
  return groups.find(g => g.type === type);
}

// get banners of type
export function getBannersByType(groups = [], type: string) {
  return getBannerGroup(groups, type)?.banners || [];
}

export function mapToTileData(product: any, treat3pItemsAsSponsored: boolean = false) {
  return {
    product: product,
    productCode: product?.code || product?.productCode,

    amplienceProductBaseUrl: product?.amplienceProductBaseUrl,

    brandName: resolveLocalized(product, 'brandName'),

    name: resolveLocalized(product, 'name'),

    productTags: product?.productTags || [],
    miraklSponsored: (treat3pItemsAsSponsored && product?.offerId) || false,
  };
}

export function resolveLocalized(product: any, baseKey: string) {
  const value = product?.[baseKey];

  if (typeof value === 'object') {
    return value?.[lang] || value?.en || value?.ar || '';
  }

  return (
    value ||
    product?.[`${baseKey}${language}`] ||
    product?.[`${baseKey === 'brandName' ? 'brand' : baseKey}${language}`] ||
    ''
  );
}
export function normalizeBannerGroup(metaData: any, banner: any) {
  // pick source
  const group = metaData?.displayBanners?.[0] ?? (Array.isArray(banner) ? banner[0] : banner) ?? null;

  if (!group) return null;

  const type = group.type || group.bannerType || group.creativeFormat || '';

  return {
    type,
    banners: group.banners || [],
  };
}

export function getNativeBannerCards(groups = []) {
  return groups.filter(g => g.bannerType === 'NATIVE_BANNER_CARD').flatMap(g => g.banners || []);
}

export function getFullWidthRowConfig(viewType): string {
  if (!isMobileWidth()) return '2,5';

  return viewType === 'grid-view' ? '2,4' : '1,3';
}

export function getItemsPerRow(viewType) {
  if (isMobileWidth()) {
    return viewType === 'grid-view' ? 2 : 1;
  }
  return 4;
}

export function getCardConfig(viewType): string {
  if (!isMobileWidth()) return miraklads?.miraklAdsPlpCardPositions || miraklads?.miraklAdsPlpFullWidthPositions;

  return viewType === 'grid-view' ? '1,3' : '1,2';
}

export function parseBannerIndexes(indexConfig: string | number[] | number = []): number[] {
  if (Array.isArray(indexConfig)) return indexConfig;

  if (typeof indexConfig === 'number') return [indexConfig];

  if (typeof indexConfig === 'string') {
    return indexConfig
      .split(',')
      .map(i => Number(i.trim()))
      .filter(i => !isNaN(i));
  }

  return [];
}

export function injectBannerCards({ products = [], bannerCards = [], indexes = [] }) {
  if (!products.length || !bannerCards.length || !indexes.length) {
    return products;
  }

  const bannerMap = new Map<number, any>();

  indexes.forEach((slot, idx) => {
    if (slot > 0 && bannerCards[idx]) {
      bannerMap.set(slot, bannerCards[idx]);
    }
  });

  const result = [];
  let productIdx = 0;
  let slot = 1;

  while (productIdx < products.length) {
    const banner = bannerMap.get(slot);

    if (banner) {
      result.push({
        type: 'BANNER_CARD',
        banner,
      });
    } else {
      result.push(products[productIdx++]);
    }

    slot++;
  }

  return result;
}

export function injectFullWidthBannersByRow({ products = [], banners = [], rowConfig = [], itemsPerRow = 4 }) {
  if (!products.length || !banners.length || !rowConfig.length) {
    return products;
  }

  const totalRows = Math.ceil(products.length / itemsPerRow);

  // Only keep rows that actually exist
  const validRows = rowConfig.filter(row => row > 0 && row <= totalRows);

  const result = [];
  let bannerPointer = 0;

  for (let i = 0; i < products.length; i++) {
    result.push(products[i]);

    const currentRow = Math.ceil((i + 1) / itemsPerRow);

    const isRowEnd = (i + 1) % itemsPerRow === 0 || i === products.length - 1;

    if (isRowEnd && validRows.includes(currentRow) && banners[bannerPointer]) {
      result.push({
        type: 'FULL_WIDTH_BANNER',
        banner: banners[bannerPointer],
      });

      bannerPointer++;
    }
  }

  return result;
}
export function toBannerGroup(banner: any) {
  if (!banner) return null;

  return {
    type: banner?.creativeFormat || banner?.bannerType || '',
    banners: [banner],
  };
}

export function normalizeBannerType(group: any) {
  const type = group?.bannerType || group?.creativeFormat;

  if (type === 'NATIVE_BANNER_CARD' || type === 'NATIVE_BANNER_FULL_WIDTH') {
    return 'NATIVE';
  }

  if (type === 'SPONSORED_BRAND_VIDEO' || type === 'SPONSORED_BRAND_IMAGE') {
    return 'SPONSORED';
  }

  return 'UNKNOWN';
}

export function normalizeAllBanners(groups = []) {
  return groups.map(group => {
    const type = group.bannerType || group.creativeFormat;

    // KEEP FULL GROUP FOR SPONSORED
    if (type === 'SPONSORED_BRAND_VIDEO' || type === 'SPONSORED_BRAND_IMAGE') {
      return {
        kind: 'SPONSORED',
        group,
      };
    }

    return {
      kind: 'NATIVE',
      layout: group.bannerType === 'NATIVE_BANNER_FULL_WIDTH' ? 'FULL_WIDTH' : 'CARD',
      banners: group.banners || [],
    };
  });
}

export function getBannerConfig(viewType) {
  if (!isMobileWidth()) {
    return {
      card: miraklads?.miraklAdsPlpCardIndexPositions, // product index based
      fullWidth: miraklads?.miraklAdsPlpFullWidthDesktopRowPositions, // rows based
    };
  }

  return viewType === 'grid-view'
    ? {
        card: miraklads?.miraklAdsPlpCardIndexPositions,
        fullWidth: miraklads?.miraklAdsPlpFullWidthAppGridRowPositions,
      }
    : {
        card: miraklads?.miraklAdsPlpCardIndexPositions,
        fullWidth: miraklads?.miraklAdsPlpFullWidthAppListRowPositions,
      };
}

type TrackBannerClickParams = {
  sourceName?: string;
  bannerId?: string;
  bannerName?: string;
  brandName?: string;
  isSponsored?: boolean;
  bannerType?: string;
  bannerDestination?: string;
};

export function trackRMNBannerClick({
  sourceName = '',
  bannerId = '',
  bannerName = '',
  brandName = '',
  isSponsored = false,
  bannerType = 'NA',
  bannerDestination = '',
}: TrackBannerClickParams = {}) {
  const acc = window?.['ACC'];

  if (!acc?.mixpanel?.trackBannerClick) return;

  try {
    acc.mixpanel.trackBannerClick(sourceName, bannerId, bannerName, brandName, isSponsored, bannerType);
    acc.mixpanel.track('RMN Banner Clicked', {
      banner_name: bannerName,
      banner_destination: bannerDestination,
      banner_location: window['ACC']?.utils?.getSourceNameForCurrentPage(),
    });
  } catch (err) {
    console.warn('RMN Banner click tracking failed', err);
  }
}

const RMN_STORAGE_KEY = 'rmn_context';

export function setRMNContext(data: { banner_type?: string; source_name?: string }) {
  try {
    sessionStorage.setItem(RMN_STORAGE_KEY, JSON.stringify(data));
  } catch {
    console.warn('Failed to set RMN context');
  }
}

export function getRMNContext() {
  try {
    const data = sessionStorage.getItem(RMN_STORAGE_KEY);
    return data ? JSON.parse(data) : null;
  } catch {
    return null;
  }
}

export function clearRMNContext() {
  try {
    sessionStorage.removeItem(RMN_STORAGE_KEY);
  } catch {
    console.warn('Failed to clear RMN context');
  }
}

export const getBannerDestination = (redirectUrl?: string) => {
  const destination = window['ACC'].utils.getReferrer(redirectUrl);
  console.log(destination, 'destination');
  return destination;
};
